/**
 * SportContext - Manages sport selection and provides sport configuration across the app
 */

import React, { createContext, useContext, useState, useCallback, useEffect, useMemo } from 'react';
import { SportId, SportIdOrNull, SportConfig, getSportConfig } from '../types/sports';
import { useAvailableSportConfigs } from '../hooks/useSports';

// Read ?sport=… from current URL (used to seed initial state).
const readSportFromUrl = (available: SportConfig[]): SportIdOrNull | undefined => {
  if (typeof window === 'undefined') return undefined;
  const params = new URLSearchParams(window.location.search);
  const raw = params.get('sport');
  if (raw === null) return undefined;
  if (raw === '' || raw === 'all') return null;
  if (available.some(s => s.id === raw)) return raw as SportId;
  return undefined;
};

// Write current sport to ?sport=… without triggering a navigation.
const writeSportToUrl = (sportId: SportIdOrNull) => {
  if (typeof window === 'undefined') return;
  const url = new URL(window.location.href);
  if (sportId === null) {
    url.searchParams.delete('sport');
  } else {
    url.searchParams.set('sport', sportId);
  }
  // Only update history when path is a sport-aware page (homepage today).
  if (url.pathname === '/') {
    window.history.replaceState({}, '', url.toString());
  }
};

interface SportContextValue {
  currentSport: SportConfig | null;
  currentSportId: SportIdOrNull;
  availableSports: SportConfig[];
  setSport: (sportId: SportIdOrNull) => void;
  isMultiSportEnabled: boolean;
  isLoading: boolean;
}

const SportContext = createContext<SportContextValue | undefined>(undefined);

// Local storage key for persisting sport selection
const SPORT_STORAGE_KEY = 'aightbet_selected_sport';

interface SportProviderProps {
  children: React.ReactNode;
  defaultSport?: SportId;
}

export const SportProvider: React.FC<SportProviderProps> = ({
  children,
  defaultSport = null
}) => {
  // KAN-11809: available sports come from `public.sport_config.is_active`,
  // not env feature flags. The hook is async — we seed the active sport
  // selection in a useEffect once the list arrives (see below).
  const { sports: availableSports, isLoading: sportsLoading } = useAvailableSportConfigs();
  const isMultiSportEnabled = availableSports.length > 1;

  // Sport selection seeds AFTER `availableSports` lands so the URL/
  // localStorage match can validate against the real list. Start at
  // `defaultSport` (typically null = all-sports) and re-seed on first
  // successful load.
  const [currentSportId, setCurrentSportId] = useState<SportIdOrNull>(defaultSport);
  const [hasSeededInitial, setHasSeededInitial] = useState(false);

  const [isLoading, setIsLoading] = useState(false);

  // KAN-11809: one-shot seed once `availableSports` finishes loading.
  // Preserves the original precedence: URL ?sport= → localStorage → default.
  useEffect(() => {
    if (hasSeededInitial || sportsLoading || availableSports.length === 0) return;

    const fromUrl = readSportFromUrl(availableSports);
    if (fromUrl !== undefined) {
      setCurrentSportId(fromUrl);
      setHasSeededInitial(true);
      return;
    }
    if (typeof window !== 'undefined') {
      const stored = localStorage.getItem(SPORT_STORAGE_KEY);
      if (stored === 'null') {
        setCurrentSportId(null);
        setHasSeededInitial(true);
        return;
      }
      if (stored && availableSports.some(s => s.id === stored)) {
        setCurrentSportId(stored as SportId);
        setHasSeededInitial(true);
        return;
      }
    }
    setCurrentSportId(defaultSport);
    setHasSeededInitial(true);
  }, [availableSports, sportsLoading, hasSeededInitial, defaultSport]);

  // Memoize currentSport to prevent unnecessary recalculations and maintain reference stability
  const currentSport = useMemo(() => {
    return currentSportId ? getSportConfig(currentSportId) : null;
  }, [currentSportId]);

  // Handle sport change with toggle support
  const setSport = useCallback((sportId: SportIdOrNull) => {
    // If sportId is null, switch to "all sports" mode
    if (sportId === null) {
      console.log(`[SportContext] Switching to all sports mode`);
      setIsLoading(true);
      setCurrentSportId(null);

      // Persist to localStorage and reflect in URL
      if (typeof window !== 'undefined') {
        localStorage.setItem(SPORT_STORAGE_KEY, 'null');
      }
      writeSportToUrl(null);

      // Clear any sport-specific caches
      if (window.queryClient) {
        console.log(`[SportContext] Invalidating React Query caches for all sports mode`);
        window.queryClient.invalidateQueries({ queryKey: ['games'] });
        window.queryClient.invalidateQueries({ queryKey: ['predictions'] });
        window.queryClient.invalidateQueries({ queryKey: ['odds'] });
      }

      // Notify other parts of the app about sport change
      window.dispatchEvent(new CustomEvent('sportChanged', { detail: { sportId: null } }));
      console.log(`[SportContext] All sports mode enabled`);

      // Reset loading state after a brief delay to allow UI to update
      setTimeout(() => setIsLoading(false), 100);
      return;
    }

    // Toggle behavior: if clicking the same sport, deselect it (switch to all sports)
    if (currentSportId === sportId) {
      console.log(`[SportContext] Toggling off ${sportId}, switching to all sports mode`);
      setSport(null);
      return;
    }

    // Validate sport is available
    if (!availableSports.some(s => s.id === sportId)) {
      console.warn(`Sport ${sportId} is not available`);
      return;
    }

    console.log(`[SportContext] Switching sport from ${currentSportId} to ${sportId}`);
    setIsLoading(true);
    setCurrentSportId(sportId);

    // Persist to localStorage and reflect in URL
    if (typeof window !== 'undefined') {
      localStorage.setItem(SPORT_STORAGE_KEY, sportId);
    }
    writeSportToUrl(sportId);

    // Clear any sport-specific caches
    // This would typically trigger a re-fetch of data for the new sport
    if (window.queryClient) {
      console.log(`[SportContext] Invalidating React Query caches for sport change`);
      window.queryClient.invalidateQueries({ queryKey: ['games'] });
      window.queryClient.invalidateQueries({ queryKey: ['predictions'] });
      window.queryClient.invalidateQueries({ queryKey: ['odds'] });
    }

    // Notify other parts of the app about sport change
    window.dispatchEvent(new CustomEvent('sportChanged', { detail: { sportId } }));
    console.log(`[SportContext] Sport change complete: ${sportId}`);

    // Reset loading state after a brief delay to allow UI to update
    setTimeout(() => setIsLoading(false), 100);
  }, [availableSports, currentSportId]);

  // Listen for sport changes from other tabs/windows
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === SPORT_STORAGE_KEY && e.newValue) {
        if (e.newValue === 'null') {
          setCurrentSportId(null);
        } else {
          const newSportId = e.newValue as SportId;
          if (availableSports.some(s => s.id === newSportId)) {
            setCurrentSportId(newSportId);
          }
        }
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, [availableSports]);

  // Memoize context value to prevent unnecessary re-renders of consumers.
  // KAN-11809: `isLoading` now folds in DB-fetch state alongside sport-switch state.
  const value: SportContextValue = useMemo(() => ({
    currentSport,
    currentSportId,
    availableSports,
    setSport,
    isMultiSportEnabled,
    isLoading: isLoading || sportsLoading,
  }), [currentSport, currentSportId, availableSports, setSport, isMultiSportEnabled, isLoading, sportsLoading]);

  return (
    <SportContext.Provider value={value}>
      {children}
    </SportContext.Provider>
  );
};

/**
 * Hook to use the sport context
 */
export const useSport = () => {
  const context = useContext(SportContext);
  if (!context) {
    throw new Error('useSport must be used within a SportProvider');
  }
  return context;
};

/**
 * Hook to get the current sport configuration
 */
export const useCurrentSport = () => {
  const { currentSport } = useSport();
  return currentSport;
};

/**
 * Hook to check if a specific sport is currently selected
 */
export const useIsSport = (sportId: SportId) => {
  const { currentSport } = useSport();
  return currentSport?.id === sportId;
};

/**
 * Hook to check if the current sport has a specific feature
 */
export const useSportFeature = (feature: keyof SportConfig['features']) => {
  const { currentSport } = useSport();
  return currentSport?.features[feature] || false;
};

/**
 * Hook to get sport-specific display configuration
 */
export const useSportDisplay = () => {
  const { currentSport } = useSport();

  // Return default display config when no sport is selected
  if (!currentSport) {
    return {
      periodLabel: (period: number) => `Period ${period}`,
      scoreFormat: 'points' as const,
      timeFormat: 'minutes_seconds' as const,
      possessionIndicator: 'none' as const,
    };
  }

  return currentSport.display;
};

/**
 * Hook to format period display based on current sport
 */
export const usePeriodFormatter = () => {
  const { currentSport } = useSport();

  return useCallback((period: number, isOvertime?: boolean) => {
    if (!currentSport) {
      // Return generic period label when no sport is selected
      if (isOvertime) return 'OT';
      return `Period ${period}`;
    }
    return currentSport.display.periodLabel(period, isOvertime);
  }, [currentSport]);
};

/**
 * Hook to check if multi-sport is enabled
 */
export const useIsMultiSportEnabled = () => {
  const { isMultiSportEnabled } = useSport();
  return isMultiSportEnabled;
};